import { ChangeEvent, useState } from 'react';
import { Spinner } from '@chakra-ui/react';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { FormikProps } from 'formik';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import Menu from '@/components/Menu';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import { useAutofillFromResume } from '@/hooks/useAutofillFromResume';
import { EditableCandidate } from '@/services/candidates';
import staffMemberServices from '@/services/staffMember';
import { FetchApiIds, Question } from '@/types';
import { candidateSchema } from '@/utils/candidate';
import CandidateOverviewFormComponent from './CandidateOverviewForm.component';
import CreateStaffMemberProfileModal from '../CreateStaffMemberProfileModal';
import SectionBox from '../SectionBox';

type CandidateOverviewFormProps = {
  questions: Question[];
  candidate: EditableCandidate;
  updatedField: string | undefined;
  onSubmit: (candidate: EditableCandidate) => Promise<void>;
  setUpdatedField: (field: string) => void;
  onSubmitFromResume: (candidate: EditableCandidate) => Promise<void>;
};

const CandidateOverviewForm = ({
  questions,
  candidate,
  updatedField,
  onSubmit,
  setUpdatedField,
  onSubmitFromResume,
}: CandidateOverviewFormProps): JSX.Element => {
  const { id: candidateId } = useParams();
  const toast = useToastContext();
  const [touchedFields, setTouchedFields] = useState(new Set(''));
  const [errorMessages, setErrorMessages] = useState({});
  const [isCreateStaffMemberModalOpen, setIsCreateStaffMemberModalOpen] = useState<boolean>(false);

  const { extractFromResume, isLoading } = useAutofillFromResume(candidateId as string);

  const { data: staffMember, refetch: refetchStaffMember } = useApiFetch(
    [FetchApiIds.GET_STAFF_MEMBER_BY_PERSON, candidate.id],
    async () => staffMemberServices.getStaffMemberByPersonId({ personId: candidate.id }),
    { retry: 0 }
  );

  const hasStaffMember = Boolean(staffMember);

  const handleChangeAndMaybeSubmit = (
    form: FormikProps<EditableCandidate>,
    field: Question,
    event: ChangeEvent<HTMLInputElement>,
    submitForm?: boolean
  ): void => {
    form.handleChange(event);
    setUpdatedField(field.name);
    setTouchedFields((prev) => prev.add(field.name));
    if (submitForm) {
      form.handleSubmit();
    }
  };

  const submitFormikForm = async (values: EditableCandidate): Promise<void> => {
    if (!updatedField) {
      return;
    }

    try {
      const updatedValue = values[updatedField];
      await yup.reach(candidateSchema, updatedField).validate(updatedValue);
    } catch (error: any) {
      if (error instanceof yup.ValidationError) {
        setErrorMessages((prev) => ({
          ...prev,
          [updatedField]: error.message,
        }));
        return;
      }
    }

    try {
      await onSubmit(values);

      if (values.status === 'HIRED' && !hasStaffMember) {
        setIsCreateStaffMemberModalOpen(true);
      }
    } finally {
      setUpdatedField('');

      setErrorMessages((prev) => ({
        ...prev,
        [updatedField]: '',
      }));
    }
  };

  const handleCreateStaffMember = (): void => {
    refetchStaffMember();
  };

  const updateCandidateFromResume = async (response: string): Promise<void> => {
    const lines = response
      .split('\n')
      .filter((line) => line.trim())
      .map((line) => line.replace(/^\d+\.\s*/, ''));
    const result = {} as Partial<EditableCandidate>;

    const keyMappings: Record<
      string,
      keyof Pick<
        EditableCandidate,
        | 'firstName'
        | 'lastName'
        | 'email'
        | 'mobilePhone'
        | 'country'
        | 'city'
        | 'developerType'
        | 'experience'
        | 'englishLevel'
        | 'salaryExpectation'
        | 'githubUrl'
        | 'linkedinUrl'
      >
    > = {
      'First Name': 'firstName',
      'Last Name': 'lastName',
      Email: 'email',
      'Mobile Number': 'mobilePhone',
      Country: 'country',
      City: 'city',
      'Developer type': 'developerType',
      'Years of experience': 'experience',
      'English level': 'englishLevel',
      'Expected salary': 'salaryExpectation',
      'Github URL': 'githubUrl',
      'Linkedin URL': 'linkedinUrl',
    };

    lines.forEach((line) => {
      const match = line.match(/^([^:]+):\s*(.+)$/);
      if (match) {
        const [, key, originalValue] = match;
        const personTypeKey = keyMappings[key.trim()];
        const value = originalValue.trim();

        if (personTypeKey) {
          if (personTypeKey === 'experience') {
            const years = value.match(/(\d+(?:\.\d+)?)/)?.[1] || '0';
            result[personTypeKey] = `${years} years`;
          } else if (personTypeKey === 'salaryExpectation') {
            result[personTypeKey] = value.toLowerCase().includes('not provided') ? '' : value;
          } else if (personTypeKey === 'englishLevel') {
            result[personTypeKey] = value.toLowerCase().includes('not provided') ? 'NONE' : value;
          } else {
            result[personTypeKey] = value.toLowerCase().includes('not provided') ? '' : value;
          }
        }
      }
    });

    await onSubmitFromResume({ ...candidate, ...result });
  };

  const handleAutofillBasicInfo = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();

    try {
      const basicInfo = await extractFromResume('basicInfo');

      await updateCandidateFromResume(basicInfo);
    } catch (error) {
      toast.showErrorFeedback(
        'Failed to autofill summary from resume. Please check if the resume is a valid PDF file.'
      );
    }
  };

  return (
    <SectionBox
      data-testid="autofill-basic-info-from-resume-button"
      title="Basic Info"
      leftElement={isLoading ? <Spinner size="xs" /> : undefined}
      rightElement={
        <Menu
          icon={MoreHorizIcon}
          menuId="delete-candidate-button"
          menuItems={[
            {
              label: 'Autofill from Resume',
              menuItemIcon: AutoFixHigh,
              callback: handleAutofillBasicInfo,
            },
          ]}
        />
      }
    >
      <CandidateOverviewFormComponent
        errorMessages={errorMessages}
        touchedFields={[...touchedFields.values()].filter(Boolean)}
        questions={questions}
        candidate={candidate}
        onSubmit={submitFormikForm}
        onChangeHandler={handleChangeAndMaybeSubmit}
      />
      <CreateStaffMemberProfileModal
        isOpen={isCreateStaffMemberModalOpen}
        onClose={() => setIsCreateStaffMemberModalOpen(false)}
        person={candidate}
        onCreateStaffMember={handleCreateStaffMember}
        message={`You’ve changed ${candidate.firstName}’s status to Hired, which means they are part of company’s staff now. Do you want to create a profile for this candidate on People Suite’s Staff Member list?`}
      />
    </SectionBox>
  );
};

export default CandidateOverviewForm;
